// ============================================================
// frontend/src/views/AllSessionsView.tsx
// ============================================================
// All sessions across all clients, with a filter chip row:
//   upcoming | past | cancelled | all
// ============================================================

import { useEffect, useState } from 'react';
import { ChevronRight } from 'lucide-react';
import { api } from '../lib/api';
import { formatDateTime } from '../lib/format';
import { StatusPill } from '../components/UI';
import { ConsiliumLoader } from '../components/ConsiliumLoader';
import type { SessionListResponse, SessionListItem } from '@consilium/shared';
import type { ViewState } from '../App';

type Filter = 'upcoming' | 'past' | 'cancelled' | 'all';
const FILTERS: { id: Filter; label: string }[] = [
  { id: 'upcoming',  label: 'Upcoming' },
  { id: 'past',      label: 'Past' },
  { id: 'cancelled', label: 'Cancelled' },
  { id: 'all',       label: 'All' },
];

interface AllSessionsViewProps {
  setView: (v: ViewState) => void;
  showError: (msg: string) => void;
}

export function AllSessionsView({ setView, showError }: AllSessionsViewProps) {
  const [filter, setFilter] = useState<Filter>('upcoming');
  const [sessions, setSessions] = useState<SessionListItem[]>([]);
  const [loading, setLoading] = useState(true);

  // Re-fetch whenever the filter changes
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    (async () => {
      try {
        const { sessions } = await api.get<SessionListResponse>(`/sessions?filter=${filter}`);
        if (!cancelled) setSessions(sessions);
      } catch (err) {
        showError(err instanceof Error ? err.message : 'Failed to load sessions');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [filter]);

  return (
    <div className="fade-in">
      <div style={{ marginBottom: 20 }}>
        <div className="mono-cap">Calendar</div>
        <h1 className="page-title">Sessions</h1>
      </div>

      <div className="chip-row" style={{ marginBottom: 20 }}>
        {FILTERS.map((f) => (
          <button
            key={f.id}
            onClick={() => setFilter(f.id)}
            className={`chip ${filter === f.id ? 'active' : ''}`}
          >
            {f.label}
          </button>
        ))}
      </div>

      {loading ? (
        <div style={{ padding: 24, display: 'flex', justifyContent: 'center' }}><ConsiliumLoader size={48} /></div>
      ) : sessions.length === 0 ? (
        <div className="card empty-state">
          <div style={{ fontSize: 14 }}>No sessions in this view.</div>
        </div>
      ) : (
        <div className="card" style={{ padding: 0 }}>
          {sessions.map((s) => (
            <div
              key={s.id} className="list-row"
              onClick={() => setView({ name: 'session', sessionId: s.id })}
            >
              <div className="row-main">
                <div className="row-primary">{s.clientName}</div>
                <div className="row-secondary">
                  {formatDateTime(s.scheduledAt)}
                  {s.agenda && ` · ${s.agenda}`}
                </div>
              </div>
              <div className="row-meta">
                {s.packageIsOneOff && <span className="pill pill-oneoff">One-off</span>}
                <StatusPill status={s.status} />
                <ChevronRight size={18} color="var(--muted-strong)" />
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
